/**
 * User drawings: the model, and the maths that decides what you clicked.
 *
 * **No rendering, no DOM, no chart library.** Everything here works in
 * price/time space and pure pixel space, which buys three things:
 *
 * 1. It is exhaustively testable without a browser. Hit-testing is the part
 *    that decides whether the tool "feels right", and feel is exactly what
 *    manual testing is worst at pinning down.
 * 2. It survives a chart-engine swap. ADR-0002 is still open pending a
 *    TradingView commercial quote; if that lands, the renderer is replaced and
 *    this file is not.
 * 3. The same model serves web and, later, React Native Skia on mobile — see
 *    `docs/07`, where the drawing model is explicitly placed in shared core.
 *
 * **Drawings are anchored in price and time, never in pixels.** A drawing
 * stored at "x=340, y=210" is wrong the instant the user scrolls. Stored at
 * "1704067200, 5423.25" it stays on the bar it was drawn against, forever,
 * which is the only behaviour a trader will accept.
 */

export type DrawingKind =
  | 'trendline'
  | 'horizontal'
  | 'vertical'
  | 'ray'
  | 'rectangle'
  | 'fib'
  | 'text'
  | 'measure';

/** A point in chart space: seconds since epoch, and a price. */
export interface Anchor {
  readonly time: number;
  readonly price: number;
}

export interface Drawing {
  readonly id: string;
  readonly kind: DrawingKind;
  readonly symbol: string;
  /**
   * One anchor for horizontal, vertical and text; two for everything else.
   * A rectangle's two anchors are opposite corners, in either order.
   */
  readonly anchors: readonly Anchor[];
  readonly text?: string;
  /** Index into the palette rather than a colour, so the theme owns the value. */
  readonly colorIndex?: number;
  readonly locked?: boolean;
  readonly createdAt: number;
}

/** How many anchors a kind needs before it is a complete drawing. */
export function anchorCount(kind: DrawingKind): 1 | 2 {
  return kind === 'horizontal' || kind === 'vertical' || kind === 'text' ? 1 : 2;
}

// ---------------------------------------------------------------------------
// Coordinate space
// ---------------------------------------------------------------------------

/**
 * The chart's current view, as two conversions.
 *
 * Supplied by whatever is rendering. Lightweight Charts gives both directly;
 * a different engine would give equivalents. Nothing below knows the difference.
 */
export interface Viewport {
  readonly timeToX: (time: number) => number | null;
  readonly priceToY: (price: number) => number | null;
  readonly xToTime: (x: number) => number | null;
  readonly yToPrice: (y: number) => number | null;
  readonly width: number;
  readonly height: number;
}

export interface Point {
  readonly x: number;
  readonly y: number;
}

/**
 * Project an anchor to pixels.
 *
 * Returns null when the anchor is outside what the view can express — off the
 * left edge after scrolling, or a price beyond the visible range. Callers skip
 * rather than clamp: a clamped anchor draws a line to the wrong place, which is
 * worse than drawing nothing.
 */
export function project(a: Anchor, v: Viewport): Point | null {
  const x = v.timeToX(a.time);
  const y = v.priceToY(a.price);
  return x === null || y === null ? null : { x, y };
}

export function unproject(p: Point, v: Viewport): Anchor | null {
  const time = v.xToTime(p.x);
  const price = v.yToPrice(p.y);
  return time === null || price === null ? null : { time, price };
}

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

/**
 * Distance from a point to a line SEGMENT, not an infinite line.
 *
 * The segment matters: clicking far past the end of a trendline, on the line's
 * extension, must not select it. Users draw short trendlines in busy charts and
 * expect the empty space beyond to be empty.
 */
export function distanceToSegment(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSq = dx * dx + dy * dy;

  // Degenerate segment — both anchors on the same pixel.
  if (lengthSq === 0) return Math.hypot(p.x - a.x, p.y - a.y);

  // Projection parameter, clamped to the segment.
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSq));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

/**
 * Distance from a point to a RAY: a segment from `a` through `b`, extended
 * forward forever. Clamped at the `a` end only.
 */
export function distanceToRay(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq === 0) return Math.hypot(p.x - a.x, p.y - a.y);

  const t = Math.max(0, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSq);
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

/** Distance to a rectangle's OUTLINE. The interior is not a hit. */
export function distanceToRectEdge(p: Point, a: Point, b: Point): number {
  const left = Math.min(a.x, b.x);
  const right = Math.max(a.x, b.x);
  const top = Math.min(a.y, b.y);
  const bottom = Math.max(a.y, b.y);

  const corners: [Point, Point][] = [
    [{ x: left, y: top }, { x: right, y: top }],
    [{ x: right, y: top }, { x: right, y: bottom }],
    [{ x: right, y: bottom }, { x: left, y: bottom }],
    [{ x: left, y: bottom }, { x: left, y: top }],
  ];

  // The interior is deliberately not a hit: a rectangle marking a supply zone
  // covers a large area, and making all of it clickable would make every click
  // inside it select the rectangle rather than reach the chart.
  return Math.min(...corners.map(([s, e]) => distanceToSegment(p, s, e)));
}

// ---------------------------------------------------------------------------
// Hit testing
// ---------------------------------------------------------------------------

/** Pixels within which a click counts. Generous, because fingers exist. */
export const HIT_TOLERANCE = 8;
/** Handles get a larger target than the line they sit on. */
export const HANDLE_TOLERANCE = 10;

export interface HitResult {
  readonly drawing: Drawing;
  /** Which anchor was grabbed, or null when the body was. */
  readonly anchorIndex: number | null;
  readonly distance: number;
}

/**
 * What is under the cursor.
 *
 * **Anchors win over bodies, and later drawings win over earlier ones.** Both
 * matter: a user who clicks an endpoint means to move that endpoint, not the
 * whole line, and the drawing they just made is the one they are most likely
 * reaching for.
 */
export function hitTest(
  point: Point,
  drawings: readonly Drawing[],
  v: Viewport,
  tolerance = HIT_TOLERANCE,
): HitResult | null {
  let best: HitResult | null = null;

  // Reverse order: the most recently added is on top.
  for (let i = drawings.length - 1; i >= 0; i--) {
    const d = drawings[i]!;
    const points = d.anchors.map((a) => project(a, v));
    if (points.some((p) => p === null)) continue;
    const pts = points as Point[];

    // Handles first, and they short-circuit — grabbing an endpoint is an
    // unambiguous intent, so no body hit should beat it.
    for (let ai = 0; ai < pts.length; ai++) {
      const dist = Math.hypot(point.x - pts[ai]!.x, point.y - pts[ai]!.y);
      if (dist <= HANDLE_TOLERANCE) {
        return { drawing: d, anchorIndex: ai, distance: dist };
      }
    }

    const dist = bodyDistance(point, d, pts, v);
    if (dist <= tolerance && (best === null || dist < best.distance)) {
      best = { drawing: d, anchorIndex: null, distance: dist };
    }
  }

  return best;
}

function bodyDistance(p: Point, d: Drawing, pts: Point[], v: Viewport): number {
  switch (d.kind) {
    case 'horizontal':
      return Math.abs(p.y - pts[0]!.y);
    case 'vertical':
      return Math.abs(p.x - pts[0]!.x);
    case 'text':
      return Math.hypot(p.x - pts[0]!.x, p.y - pts[0]!.y);
    case 'ray':
      return distanceToRay(p, pts[0]!, pts[1]!);
    case 'rectangle':
      return distanceToRectEdge(p, pts[0]!, pts[1]!);
    case 'fib':
      return fibDistance(p, pts[0]!, pts[1]!, v);
    case 'trendline':
    case 'measure':
      return distanceToSegment(p, pts[0]!, pts[1]!);
  }
}

// ---------------------------------------------------------------------------
// Fibonacci
// ---------------------------------------------------------------------------

/**
 * The retracement levels, in the order every charting package shows them.
 *
 * 0 and 1 are the anchors themselves. 0.5 is not a Fibonacci ratio at all —
 * it is there because the convention includes it and a trader looking for the
 * half-retracement would otherwise think the tool was broken.
 */
export const FIB_LEVELS = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1] as const;

export interface FibLevel {
  readonly ratio: number;
  readonly price: number;
}

/** Level prices between two anchors. `a` is 0, `b` is 1. */
export function fibLevels(a: Anchor, b: Anchor): FibLevel[] {
  const span = b.price - a.price;
  return FIB_LEVELS.map((ratio) => ({ ratio, price: a.price + span * ratio }));
}

function fibDistance(p: Point, a: Point, b: Point, _v: Viewport): number {
  // Each level is a horizontal line spanning the anchors' x range.
  const left = Math.min(a.x, b.x);
  const right = Math.max(a.x, b.x);
  if (p.x < left - HIT_TOLERANCE || p.x > right + HIT_TOLERANCE) return Infinity;

  const span = b.y - a.y;
  return Math.min(...FIB_LEVELS.map((r) => Math.abs(p.y - (a.y + span * r))));
}

// ---------------------------------------------------------------------------
// The measure tool
// ---------------------------------------------------------------------------

export interface Measurement {
  readonly priceChange: number;
  readonly percentChange: number;
  readonly bars: number;
  readonly seconds: number;
  readonly direction: 'up' | 'down' | 'flat';
}

/**
 * What the measure tool reports.
 *
 * Descriptive only — a distance and a duration. It says nothing about whether
 * the move is likely to continue, because AEGIS is not a registered adviser and
 * a measurement that editorialised would be the easiest place to slip.
 */
export function measure(a: Anchor, b: Anchor, barSeconds: number): Measurement {
  const priceChange = b.price - a.price;
  const seconds = Math.abs(b.time - a.time);
  return {
    priceChange,
    // Guard against a zero anchor price, which a percentage cannot describe.
    percentChange: a.price === 0 ? 0 : (priceChange / Math.abs(a.price)) * 100,
    bars: barSeconds > 0 ? Math.round(seconds / barSeconds) : 0,
    seconds,
    direction: priceChange > 0 ? 'up' : priceChange < 0 ? 'down' : 'flat',
  };
}

// ---------------------------------------------------------------------------
// Mutation
// ---------------------------------------------------------------------------

/** Move one anchor. Returns a new drawing; the input is untouched. */
export function moveAnchor(d: Drawing, index: number, to: Anchor): Drawing {
  if (d.locked) return d;
  const anchors = d.anchors.map((a, i) => (i === index ? to : a));
  return { ...d, anchors };
}

/**
 * Move the whole drawing by a delta in chart space.
 *
 * The delta is in price and time rather than pixels, so a drag that crosses a
 * zoom change does not distort the shape.
 */
export function translate(d: Drawing, dTime: number, dPrice: number): Drawing {
  if (d.locked) return d;
  return {
    ...d,
    anchors: d.anchors.map((a) => ({ time: a.time + dTime, price: a.price + dPrice })),
  };
}

/**
 * Normalise a drawing after it is created or edited.
 *
 * A trendline drawn right-to-left is stored left-to-right, so every consumer
 * can assume `anchors[0].time <= anchors[1].time` instead of each one guarding.
 * Rectangles are normalised the same way. Fibonacci is NOT: its direction is
 * meaningful — 0 is where the user started, and flipping it inverts the levels.
 */
export function normalise(d: Drawing): Drawing {
  if (d.kind === 'fib' || d.anchors.length < 2) return d;

  const [a, b] = d.anchors as [Anchor, Anchor];
  if (a.time <= b.time) return d;
  return { ...d, anchors: [b, a] };
}
